<script lang="ts">
import { cloneDeep, isEmpty } from 'lodash'
import { defineComponent, reactive, Ref, ref, shallowRef } from 'vue'

import { Stat } from '@collector/sportsapi-client-legacy'
import { UpdateEventParticipants } from '@mobile/ActionQueue/Actions/Actions'
import UpdateEvent from '@mobile/ActionQueue/Actions/Event/UpdateEvent'
import * as popup from '@mobile/globalState/popup'
import {
  participantDiff,
  resolveUpdateEventDetailsObjectFromEventDetailsDiff,
  resolveUpdateEventObjectFromEventDiff,
} from '@mobile/reusables/eventUtils'
import PopupControls from '@mobile/views/Relation/Shared/Popup/PopupControls.vue'
import StackedPopup from '@mobile/views/Relation/Shared/Popup/StackedPopup.vue'
import EventPreparationEventDetails from '@mobile/views/Relation/Shared/Popups/EventPreparation/EventPreparationEventDetails.vue'
import EventPreparationTeamDetails from '@mobile/views/Relation/Shared/Popups/EventPreparation/EventPreparationTeamDetails.vue'
import EventHeaderToggle from '@mobile/views/Relation/Shared/Popups/EventSettings/EventHeaderToggle.vue'
import Skins from '@mobile/views/Relation/Shared/Popups/Skins/Skins.vue'
import { ProbableLinkedParticipant } from '@mobile/views/Relation/Shared/RelationDependencies/types'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'
import Formations from '@mobile/views/Relation/Sports/Soccer/Popups/Formation/Formation.vue'

import { EventPreparationChildPopupType } from '../EventPreparation/EventPreparationChildPopupType'
import EventPreparationEventStats from '../EventPreparation/EventPreparationEventStats.vue'
import Lineups from '../Lineups/Lineups.vue'

export default defineComponent({
  components: {
    EventHeaderToggle,
    EventPreparationTeamDetails,
    PopupControls,
    EventPreparationEventDetails,
    EventPreparationEventStats,
    Skins,
    StackedPopup,
    Lineups,
    Formations,
  },
  setup() {
    const {
      probableEvent,
      actionQueue,
      sport,
      probableLinkedFirstParticipant,
      probableLinkedSecondParticipant,
    } = useEventInjections()
    const probableFirstParticipant =
      probableLinkedFirstParticipant.value.getParticipant()
    const probableSecondParticipant =
      probableLinkedSecondParticipant.value.getParticipant()
    const staticFirstParticipant = reactive(
      cloneDeep(probableFirstParticipant.value),
    )
    const staticSecondParticipant = reactive(
      cloneDeep(probableSecondParticipant.value),
    )

    const preparationEventDetailsData = ref(cloneDeep(probableEvent.value))

    const openedPopup: Ref<EventPreparationChildPopupType | null> = ref(null)
    const selectedParticipant: Ref<ProbableLinkedParticipant | null> =
      shallowRef(null)

    function updateEvent(): void {
      const event = resolveUpdateEventObjectFromEventDiff(
        probableEvent.value,
        preparationEventDetailsData.value,
      )
      const details = resolveUpdateEventDetailsObjectFromEventDetailsDiff(
        probableEvent.value.details,
        preparationEventDetailsData.value.details,
        sport.value.sportsAPIEntity,
      )

      if (!isEmpty(event) || !isEmpty(details)) {
        actionQueue.value.add(
          new UpdateEvent({
            event,
            details,
          }),
        )
      }
    }

    function updateEventParticipants(): void {
      actionQueue.value.add(
        new UpdateEventParticipants({
          data: [
            participantDiff(
              probableFirstParticipant.value,
              staticFirstParticipant,
            ),
            participantDiff(
              probableSecondParticipant.value,
              staticSecondParticipant,
            ),
          ],
        }),
      )
    }

    function save(): void {
      updateEvent()
      updateEventParticipants()

      popup.hide()
    }

    function cancel(): void {
      popup.hide()
    }

    function handleOpenPopup({
      popupType,
      probableLinkedParticipant,
    }: {
      popupType: EventPreparationChildPopupType
      probableLinkedParticipant: ProbableLinkedParticipant
    }): void {
      openedPopup.value = popupType
      selectedParticipant.value = probableLinkedParticipant
    }

    function isPopupOpened(popupType: EventPreparationChildPopupType): boolean {
      return openedPopup.value === popupType
    }

    function closeChildPopup(): void {
      openedPopup.value = null
    }

    function participantStatsUpdated([{ getParticipant }, stat]: [
      ProbableLinkedParticipant,
      Stat,
    ]): void {
      const participant = getParticipant().value

      ;[staticFirstParticipant, staticSecondParticipant].forEach(
        (staticParticipant) => {
          const statToUpdate = staticParticipant.stats.find(
            ({ id }) => id === stat.id,
          )

          if (statToUpdate) {
            statToUpdate.value = participant.id === staticParticipant.id ? 1 : 0
          }
        },
      )
    }

    return {
      save,
      cancel,
      isPopupOpened,
      closeChildPopup,
      handleOpenPopup,
      selectedParticipant,
      preparationEventDetailsData,
      EventPreparationChildPopupType,
      probableLinkedFirstParticipant,
      probableLinkedSecondParticipant,
      staticFirstParticipant,
      staticSecondParticipant,
      participantStatsUpdated,
    }
  },
})
</script>

<template>
  <div>
    <EventHeaderToggle :name="'Team details'">
      <EventPreparationTeamDetails
        :event="preparationEventDetailsData"
        @openPopup="handleOpenPopup"
      />
    </EventHeaderToggle>
    <EventHeaderToggle :name="'Event Info'">
      <EventPreparationEventDetails :event="preparationEventDetailsData" />
    </EventHeaderToggle>

    <EventHeaderToggle :name="'Event Stats'">
      <EventPreparationEventStats
        :event="preparationEventDetailsData"
        @participantStatsUpdated="participantStatsUpdated"
      />
    </EventHeaderToggle>

    <PopupControls
      @confirm="save()"
      @cancel="cancel()"
    />

    <template v-if="selectedParticipant">
      <StackedPopup v-if="isPopupOpened(EventPreparationChildPopupType.Skins)">
        <Skins
          :probableLinkedParticipant="selectedParticipant"
          @close="closeChildPopup"
        />
      </StackedPopup>
      <StackedPopup
        v-if="isPopupOpened(EventPreparationChildPopupType.Lineups)"
      >
        <Lineups
          :probableLinkedParticipant="selectedParticipant"
          @close="closeChildPopup"
        />
      </StackedPopup>
      <StackedPopup
        v-if="isPopupOpened(EventPreparationChildPopupType.Formations)"
      >
        <Formations
          :probableLinkedParticipant="selectedParticipant"
          @close="closeChildPopup"
        />
      </StackedPopup>
    </template>
  </div>
</template>
